import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormGroup, Validators, FormBuilder } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { PaginationService } from '../../../../helpers/pagination.service';
import { AccountingService } from '../../accounting.service';
import { Subscription } from 'rxjs';
import {
  AccountResponseType,
  IAccount,
  IAccountByIdResponseObject,
  IAccountByIdRespose,
  IAccountLogs,
  IAccountLogsResponseType,
  ISaveContactResponse,
  ISaveResponseType,
} from '../../models/account.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ChangeDetectorRef, OnDestroy } from '@angular/core';

@Component({
  selector: 'app-account-details',
  templateUrl: './account-details.component.html',
  styleUrls: [
    './account-details.component.scss',
    '../../accounting-table.scss',
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AccountDetailsComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  queryParams: any;
  accountForm!: FormGroup;

  accountId: number = 0;
  accountData: IAccount | null = null;
  isAccountDetailsEditable: boolean = false;
  private dataSubscription: Subscription = new Subscription();
  private routeParamSubscription: Subscription = new Subscription();

  pageLimit = 1000;
  currentPageSize: number = 20;
  currentPageIndex: number = 1;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  logsData: IAccountLogs[] = [];
  totalRecords: number = 0;
  dataSource = new MatTableDataSource<any>([]);
  accountLogData: IAccountLogs[] = [];
  displayedColumns = ['date', 'name', 'description'];
  status: string | undefined;
  showLogForm: boolean = false;
  creationDate: string | undefined;
  iban: string | undefined;
  isClient: boolean | undefined;
  exactOnlineID: string | null | undefined;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private paginationService: PaginationService,
    private fb: FormBuilder,
    private _snackBar: MatSnackBar,
    private _accountingService: AccountingService,
    private cdr: ChangeDetectorRef,
  ) {
    this.getAccountDetails();
    this.initializeForm();
  }

  ngOnInit() {
    this.routeParamSubscription = this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      if (id) {
        this.accountId = Number(id);
        this.getAccountDetailsById();
      }
    });
    if (history.state.data) {
      this.accountData = history.state.data;
    }
  }

  getAccountDetails() {
    this.dataSubscription = this._accountingService.accountDataById$.subscribe(
      (data: IAccount | null) => {
        this.accountData = data;
        if (data && data.profileId) {
          this.status = data?.statusDesc;
          this.creationDate = data?.creationDate;
          this.iban = data?.iban;
          this.isClient = data?.isClient;
          this.exactOnlineID = data?.exactOnlineID;
          // this.bindFormData();
        }
      },
    );
  }

  initializeForm() {
    this.accountForm = this.fb.group({
      id: [''],
      profileId: [{ value: '', disabled: true }],
      firstName: ['', Validators.required],
      surName: [''],
      email: [''],
      phone: [''],
      houseNumber: [''],
      address: [''],
      houseNumberAdd: [''],
      postCode: [],
      city: [''],
      iban: [''],
      bankName: [''],
      creationDate: [{ value: '', disabled: true }],
      isClient: [{ value: '', disabled: true }],
      exactOnlineID: [{ value: '', disabled: true }],
    });
  }

  bindFormData() {
    if (this.accountData) {
      this.accountForm.patchValue({
        id: this.accountData.id,
        profileId: this.accountData.profileId,
        firstName: this.accountData.firstName || '',
        surName: this.accountData.surName || '',
        email: this.accountData.email || '',
        phone: this.accountData.phone || '',
        houseNumber: this.accountData.houseNumber || '',
        address: this.accountData.address || '',
        houseNumberAdd: this.accountData.houseNumberAdd || '',
        city: this.accountData.city || '',
        postCode: this.accountData.postCode,
        iban: this.accountData.iban || '',
        bankName: this.accountData.bankName || '',
        creationDate: this.creationDate || '',
        isClient: this.isClient ? 'Klant' : 'Handyman',
        exactOnlineID: this.exactOnlineID,
      });
    }
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  editAccountDetails() {
    this.isAccountDetailsEditable = !this.isAccountDetailsEditable;
    this.accountForm.controls['eolContactId']?.getRawValue();
  }

  updateLogs() {
    this.showLogForm = !this.showLogForm;
  }

  saveAccountDetails() {
    if (this.accountForm.valid) {
      const formData = this.accountForm.getRawValue();
      this._accountingService.saveContact(formData).subscribe({
        next: (response: ISaveResponseType) => {
          if (!response.isError) {
            this._snackBar.open(`${response.value}`, 'Close', {
              duration: 3000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
            });
            this.accountForm.patchValue(this.accountForm);
            sessionStorage.removeItem('accountData');
            this.getAccountDetailsById();
          } else {
            this._snackBar.open(
              `Unable to update accoundetails. Please try again...`,
              'Close',
              {
                duration: 3000,
                horizontalPosition: 'center',
                verticalPosition: 'top',
              },
            );
          }
        },
        error: (error) => {
          this._snackBar.open('Error while saving account details', 'Close', {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
          });
        },
      });
    }
  }

  getAccountDetailsById() {
    this._accountingService.getContactsById(this.accountId).subscribe({
      next: (res: IAccountByIdResponseObject) => {
        if (!res.isError) {
          this.accountData = res.value;
          this._snackBar.open('Fetched updated account details', 'Close', {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
          });
          this.accountData
            ? {
                ...this.accountData,
                exactOnlineID: this.exactOnlineID,
                creationDate: this.creationDate,
                isClient: this.isClient,
              }
            : null;
          sessionStorage.setItem(
            'accountData',
            JSON.stringify(this.accountData),
          );
          this.bindFormData();
          this.cdr.detectChanges();
          this.getContactsLogsData();
          this.isAccountDetailsEditable = false;
        } else {
          this._snackBar.open(`Error while fetching account details`, 'Close', {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
          });
        }
      },
      error: (error) => {
        this._snackBar.open('Error while getting account details', 'Close', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });
      },
    });
  }

  cancelEditAccountDetails() {
    this.isAccountDetailsEditable = false;
    this.bindFormData();
  }

  getContactsLogsData() {
    const profileId = this.accountForm.controls['profileId'].getRawValue();
    if (this.accountId) {
      this._accountingService.getContactLogs(profileId).subscribe({
        next: (data: IAccountLogsResponseType) => {
          const logData = data.value;
          if (!data.isError) {
            this.dataSource.data = logData;
            // this.totalRecords = data.value.totalRecords;

            // show table view
            this.showLogForm = false;

            // Ensure paginator/sort attach AFTER DOM loads
            setTimeout(() => {
              this.dataSource.paginator = this.paginator;
              this.dataSource.sort = this.sort;
            });
            // this.dataSource.data = [...this.dataSource.data, ...logData];
            // this.accountLogData = this.dataSource.data.slice(
            //   0,
            //   this.currentPageSize,
            // );
            // this.totalRecords = data.totalCount;
            // this.paginator.length = this.totalRecords; // Set paginator length
          } else {
            this._snackBar.open(`Error while fetching Account Logs`, 'Close', {
              duration: 3000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
            });
          }
        },
        error: (error) => {
          this._snackBar.open('Failed to fetch contact logs', 'Close', {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
          });
        },
      });
    } else {
      this._snackBar.open('No contact ID available to fetch logs.', 'Close', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'top',
      });
    }
  }

  getLogData(data: any): void {
    const body = {
      id: this.accountData?.id,
      profileId: this.accountData?.profileId,
      comment: data.comment,
      description: data.description,
      name: sessionStorage.getItem('userName'),
    };
    this._accountingService.updateContactLogData(body).subscribe({
      next: (logs: any) => {
        if (!logs.isError) {
          this._snackBar.open('Successfully updated contacts logs', 'Close', {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
          });
          this.dataSource.data = [];
          this.getContactsLogsData();
          this.showLogForm = !this.showLogForm;
          this.cdr.detectChanges();
        } else {
          this._snackBar.open(`${logs.value.value}`, 'Close', {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
          });
        }
      },
      error: () => {
        this._snackBar.open('Error in fetching Contacts logs', 'Close', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });
      },
    });
  }

  // Handle page changes
  handlePageEvent(event: any) {
    this.currentPageIndex = event.pageIndex;
    this.currentPageSize = event.pageSize;
    this.getContactsLogsData(); // Fetch data for the new page
  }

  goToAccountingPage() {
    this.router.navigate(['/klusup/accounting/account']);
  }

  importData(data: any) {
    console.log('Imported data:', data);
  }

  ngOnDestroy(): void {
    if (this.dataSubscription) {
      this.dataSubscription.unsubscribe();
    }
    if (this.routeParamSubscription) {
      this.routeParamSubscription.unsubscribe();
    }
  }
}
